import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Search, Plus, Edit, Trash2, Shield, Mail, Calendar,
  Users, Loader2, Building2, UserPlus, Key, CheckCircle,
  XCircle, Clock, ChevronRight, Settings, UserCog
} from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { toast } from 'sonner';
import { getOrganizationService, type Organization } from '../../services/organizationService';
import { getUserService, type User, type CreateUserRequest } from '../../services/userService';
import { useRBAC } from '../../components/auth/RBACContext';

type TabType = 'users' | 'roles' | 'groups';

// Role definitions with permissions
const systemRoles = [
  {
    id: 'root',
    name: 'Root Admin',
    description: 'Super administrator with full system access',
    color: 'bg-red-100 text-red-700',
    permissions: ['*']
  },
  {
    id: 'admin',
    name: 'Organization Admin',
    description: 'Full access to organization settings and users',
    color: 'bg-purple-100 text-purple-700',
    permissions: ['org:manage', 'users:manage', 'processes:manage']
  },
  {
    id: 'manager',
    name: 'Process Manager',
    description: 'Can manage processes and tasks',
    color: 'bg-blue-100 text-blue-700',
    permissions: ['processes:manage', 'tasks:manage']
  },
  {
    id: 'user',
    name: 'Standard User',
    description: 'Basic access to assigned tasks and processes',
    color: 'bg-green-100 text-green-700',
    permissions: ['tasks:view', 'processes:view']
  },
];

// Group definitions
const systemGroups = [
  { id: '1', name: 'Administrators', description: 'System administrators', memberCount: 2, icon: '🛡️' },
  { id: '2', name: 'Process Managers', description: 'Process design and management', memberCount: 8, icon: '⚙️' },
  { id: '3', name: 'Finance Team', description: 'Financial operations', memberCount: 5, icon: '💰' },
  { id: '4', name: 'HR Team', description: 'Human resources', memberCount: 4, icon: '👥' },
  { id: '5', name: 'Support', description: 'Customer support', memberCount: 6, icon: '🎧' },
];

const UsersPage: React.FC = () => {
  // RBAC context. Super_admin / platform_support browse all orgs; org-scoped
  // admins (org_admin / task_org_admin) are confined to their own org.
  const {
    isSuperAdmin,
    isPlatformSupport,
    organizations: rbacOrgs,
    currentOrgId,
    hasOrgRole,
    refreshOrganizations,
  } = useRBAC();
  // Only super_admin can create/delete orgs; org_admin may also edit org
  // settings. task_org_admin manages users only (no org-settings controls).
  const canManageOrgSettings = isSuperAdmin || hasOrgRole('org_admin');

  // Tab state - removed 'organizations' from tabs
  const [selectedTab, setSelectedTab] = useState<TabType>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [orgSearchQuery, setOrgSearchQuery] = useState('');

  // Organization state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgLoadingMore, setOrgLoadingMore] = useState(false);
  const [orgPage, setOrgPage] = useState(0);
  const [orgHasMore, setOrgHasMore] = useState(false);
  const [orgTotalCount, setOrgTotalCount] = useState(0);
  const ORG_PAGE_SIZE = 20;
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [orgFormData, setOrgFormData] = useState({ name: '', displayName: '', url: '', domain: '' });
  const [orgLogo, setOrgLogo] = useState<File | null>(null);

  // User state
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    orgId: '',
  });

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'org' | 'user'; item: Organization | User } | null>(null);

  // Password reset
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Loading states
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch organizations with pagination
  const fetchOrganizations = useCallback(async (page: number = 0, append: boolean = false) => {
    if (append) {
      setOrgLoadingMore(true);
    } else {
      setOrgLoading(true);
    }
    try {
      const service = getOrganizationService();
      const response = await service.getOrganizations(orgSearchQuery, page, ORG_PAGE_SIZE);
      const newOrgs = response.content || [];

      if (append) {
        setOrganizations(prev => [...prev, ...newOrgs]);
      } else {
        setOrganizations(newOrgs);
        // Select first org if none selected
        if (!selectedOrg && newOrgs.length > 0) {
          setSelectedOrg(newOrgs[0]);
        }
      }

      setOrgPage(page);
      setOrgTotalCount(response.count || 0);
      // Calculate if there are more pages
      const loadedCount = append ? organizations.length + newOrgs.length : newOrgs.length;
      setOrgHasMore(loadedCount < (response.count || 0));
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setOrgLoading(false);
      setOrgLoadingMore(false);
    }
  }, [orgSearchQuery, selectedOrg, organizations.length]);

  // Fetch users for selected org
  const fetchUsers = useCallback(async () => {
    if (!selectedOrg) return;
    setUsersLoading(true);
    try {
      const service = getUserService();
      const response = await service.getUsers(selectedOrg.id, searchQuery, 0, 100);
      setUsers(response.content || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [selectedOrg, searchQuery]);

  // Reload the org list using the right source for the current user.
  const reloadOrganizations = useCallback(() => {
    if (isSuperAdmin || isPlatformSupport) {
      fetchOrganizations();
    } else {
      // org-scoped admin: re-pull their own org(s); the mirror effect below
      // copies the refreshed list into local state.
      refreshOrganizations({ force: true });
    }
  }, [isSuperAdmin, isPlatformSupport, fetchOrganizations, refreshOrganizations]);

  // Load organizations on mount. Super_admin / platform_support browse the
  // full catalog; org-scoped admins (org_admin / task_org_admin) only ever see
  // their OWN org, sourced from the JWT / per-user endpoint — never the
  // privileged /organizations catalog (which 403s and exposes other orgs).
  useEffect(() => {
    if (isSuperAdmin || isPlatformSupport) {
      fetchOrganizations();
    } else {
      refreshOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, isPlatformSupport]);

  // Mirror the org-scoped admin's own org(s) from RBAC into local state.
  useEffect(() => {
    if (isSuperAdmin || isPlatformSupport) return;
    const own: Organization[] = rbacOrgs.map((o) => ({ id: o.id, name: o.name, displayName: o.name }));
    setOrganizations(own);
    setOrgTotalCount(own.length);
    setOrgHasMore(false);
    setSelectedOrg((prev) => prev ?? own.find((o) => o.id === currentOrgId) ?? own[0] ?? null);
  }, [rbacOrgs, isSuperAdmin, isPlatformSupport, currentOrgId]);

  // Load users when org changes or tab switches to users
  useEffect(() => {
    if (selectedTab === 'users' && selectedOrg) {
      fetchUsers();
    }
  }, [selectedTab, selectedOrg]);

  // Organization handlers
  const handleSaveOrg = async () => {
    if (!orgFormData.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setActionLoading(true);
    try {
      const service = getOrganizationService();
      const payload = {
        name: orgFormData.name,
        displayName: orgFormData.displayName,
        url: orgFormData.url || undefined,
        domains: orgFormData.domain ? [orgFormData.domain] : undefined,
      };
      if (editingOrg) {
        await service.updateOrganization(editingOrg.id, payload);
        toast.success('Organization updated');
      } else {
        await service.createOrganization(payload);
        toast.success('Organization created');
      }
      setShowOrgModal(false);
      setEditingOrg(null);
      setOrgFormData({ name: '', displayName: '', url: '', domain: '' });
      setOrgLogo(null);
      reloadOrganizations();
    } catch (error) {
      toast.error('Failed to save organization');
    } finally {
      setActionLoading(false);
    }
  };

  // User handlers
  const handleSaveUser = async () => {
    if (!userFormData.username.trim() || !userFormData.email.trim()) {
      toast.error('Username and email are required');
      return;
    }
    if (!editingUser && !userFormData.password) {
      toast.error('Password is required for new users');
      return;
    }
    setActionLoading(true);
    try {
      const service = getUserService();
      if (editingUser) {
        await service.updateUser({
          id: editingUser.id,
          email: userFormData.email,
          firstName: userFormData.firstName,
          lastName: userFormData.lastName,
        });
        toast.success('User updated');
      } else {
        await service.createUser({
          ...userFormData,
          orgId: selectedOrg?.id || '',
        } as CreateUserRequest);
        toast.success('User created');
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserFormData({ username: '', email: '', firstName: '', lastName: '', password: '', orgId: '' });
      fetchUsers();
    } catch (error) {
      toast.error('Failed to save user');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      if (deleteTarget.type === 'org') {
        await getOrganizationService().deleteOrganization((deleteTarget.item as Organization).id);
        toast.success('Organization deleted');
        if (selectedOrg?.id === (deleteTarget.item as Organization).id) {
          setSelectedOrg(null);
        }
        reloadOrganizations();
      } else {
        await getUserService().deleteUser((deleteTarget.item as User).id);
        toast.success('User deleted');
        fetchUsers();
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  // Password reset handler
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setActionLoading(true);
    try {
      await getUserService().resetPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  // Open modals
  const openOrgModal = (org?: Organization) => {
    if (org) {
      setEditingOrg(org);
      setOrgFormData({
        name: org.name || '',
        displayName: org.displayName || '',
        url: org.url || '',
        domain: org.domains?.[0] || ''
      });
    } else {
      setEditingOrg(null);
      setOrgFormData({ name: '', displayName: '', url: '', domain: '' });
    }
    setOrgLogo(null);
    setShowOrgModal(true);
  };

  const openUserModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setUserFormData({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        password: '',
        orgId: selectedOrg?.id || '',
      });
    } else {
      setEditingUser(null);
      setUserFormData({ username: '', email: '', firstName: '', lastName: '', password: '', orgId: selectedOrg?.id || '' });
    }
    setShowUserModal(true);
  };

  // Stats
  const stats = {
    totalOrgs: organizations.length,
    totalUsers: users.length,
    activeUsers: users.filter(u => u.enabled).length,
    pendingUsers: users.filter(u => !u.emailVerified).length,
  };

  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org =>
    (org.name?.toLowerCase() || '').includes(orgSearchQuery.toLowerCase()) ||
    (org.displayName?.toLowerCase() || '').includes(orgSearchQuery.toLowerCase())
  );

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-indigo-100 rounded-xl">
                  <Users size={22} className="text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">IAM</h1>
                  <p className="text-sm text-gray-500">Manage tenants, users, groups and roles</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-56 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Organizations Sidebar - Fixed with separate scroll */}
            <div className="w-72 border-r border-gray-200 bg-gray-50/50 flex flex-col h-full overflow-hidden">
              {/* Sidebar Header */}
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 size={18} className="text-indigo-600" />
                    <span className="font-semibold text-gray-900">Organizations</span>
                    <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                      {stats.totalOrgs}
                    </span>
                  </div>
                  {isSuperAdmin && (
                    <button
                      onClick={() => openOrgModal()}
                      className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                      title="Add Organization"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={orgSearchQuery}
                    onChange={(e) => setOrgSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Organizations List */}
              <div className="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                {orgLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin text-indigo-600" size={24} />
                  </div>
                ) : filteredOrganizations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                    <Building2 size={32} className="mb-2 text-gray-300" />
                    <p className="text-sm">No organizations found</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredOrganizations.map((org) => (
                      <div
                        key={org.id}
                        onClick={() => setSelectedOrg(org)}
                        className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${selectedOrg?.id === org.id
                          ? 'bg-indigo-50 border-2 border-indigo-200'
                          : 'hover:bg-white border-2 border-transparent hover:border-gray-200'
                          }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedOrg?.id === org.id ? 'bg-indigo-100' : 'bg-gray-100 group-hover:bg-indigo-50'
                          }`}>
                          <Building2 size={18} className={selectedOrg?.id === org.id ? 'text-indigo-600' : 'text-gray-500 group-hover:text-indigo-500'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm truncate ${selectedOrg?.id === org.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                            {org.displayName || org.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{org.name}</p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {canManageOrgSettings && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openOrgModal(org); }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-indigo-600"
                            >
                              <Edit size={14} />
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'org', item: org }); setShowDeleteModal(true); }}
                              className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Load More Button */}
                    {orgHasMore && (
                      <button
                        onClick={() => fetchOrganizations(orgPage + 1, true)}
                        disabled={orgLoadingMore}
                        className="w-full mt-2 py-2 px-3 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {orgLoadingMore ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            Load More ({organizations.length} of {orgTotalCount})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-100 bg-gray-50/30">
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Organization</p>
                        <p className="text-lg font-bold text-gray-900 truncate">{selectedOrg?.displayName || selectedOrg?.name || 'None'}</p>
                      </div>
                      <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Building2 size={18} className="text-indigo-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Total Users</p>
                        <p className="text-lg font-bold text-gray-900">{stats.totalUsers}</p>
                      </div>
                      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                        <Users size={18} className="text-green-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Active</p>
                        <p className="text-lg font-bold text-gray-900">{stats.activeUsers}</p>
                      </div>
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CheckCircle size={18} className="text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Pending</p>
                        <p className="text-lg font-bold text-gray-900">{stats.pendingUsers}</p>
                      </div>
                      <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Clock size={18} className="text-amber-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs - removed Organizations tab */}
              <div className="border-b border-gray-200 bg-white">
                <div className="flex px-6">
                  {[
                    { key: 'users', label: 'Users', icon: Users, count: stats.totalUsers },
                    { key: 'roles', label: 'Roles', icon: Shield, count: systemRoles.length },
                    { key: 'groups', label: 'Groups', icon: UserCog, count: systemGroups.length },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSelectedTab(tab.key as TabType)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${selectedTab === tab.key
                        ? 'text-indigo-600 border-indigo-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700'
                        }`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${selectedTab === tab.key ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-auto p-6">
                {/* Users Tab */}
                {selectedTab === 'users' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Users {selectedOrg && <span className="text-gray-400 font-normal">in {selectedOrg.displayName || selectedOrg.name}</span>}
                      </h3>
                      <button
                        onClick={() => openUserModal()}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        disabled={!selectedOrg}
                      >
                        <UserPlus size={16} />
                        Invite User
                      </button>
                    </div>
                    {!selectedOrg ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                        <Building2 size={48} className="mb-4 text-gray-300" />
                        <p className="font-medium">Select an organization first</p>
                        <p className="text-sm">Choose an organization from the sidebar to manage users</p>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {usersLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="animate-spin text-indigo-600" size={24} />
                          </div>
                        ) : users.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <Users size={48} className="mb-4 text-gray-300" />
                            <p>No users found</p>
                            <p className="text-sm">Invite users to this organization</p>
                          </div>
                        ) : (
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {users.map((user) => (
                                <tr
                                  key={user.id}
                                  className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedUser?.id === user.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : 'border-l-4 border-transparent'}`}
                                  onClick={() => setSelectedUser(user)}
                                >
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
                                        {(user.firstName?.[0] || user.username[0]).toUpperCase()}
                                      </div>
                                      <div>
                                        <p className="font-medium text-gray-900">
                                          {user.firstName && user.lastName
                                            ? `${user.firstName} ${user.lastName}`
                                            : user.username}
                                        </p>
                                        <p className="text-sm text-gray-500">@{user.username}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600">{user.email}</span>
                                      {user.emailVerified && <CheckCircle size={14} className="text-green-500" />}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${user.enabled
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-600'
                                      }`}>
                                      {user.enabled ? (
                                        <><CheckCircle size={12} /> Active</>
                                      ) : (
                                        <><XCircle size={12} /> Disabled</>
                                      )}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center justify-end gap-1">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); openUserModal(user); }}
                                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-indigo-600"
                                        title="Edit"
                                      >
                                        <Edit size={16} />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedUser(user); setShowPasswordModal(true); }}
                                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-amber-600"
                                        title="Reset Password"
                                      >
                                        <Key size={16} />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: 'user', item: user }); setShowDeleteModal(true); }}
                                        className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                                        title="Delete"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Roles Tab */}
                {selectedTab === 'roles' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">System Roles</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {systemRoles.map((role) => (
                        <div key={role.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.color.replace('text-', 'bg-').replace('-700', '-100')}`}>
                                <Shield size={20} className={role.color.split(' ')[1]} />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900">{role.name}</h4>
                                <p className="text-sm text-gray-500">{role.description}</p>
                              </div>
                            </div>
                            <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400">
                              <Settings size={16} />
                            </button>
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-2">PERMISSIONS</p>
                            <div className="flex flex-wrap gap-1">
                              {role.permissions.map((perm, i) => (
                                <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                  {perm}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Groups Tab */}
                {selectedTab === 'groups' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">User Groups</h3>
                      <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
                        <Plus size={16} />
                        New Group
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      {systemGroups.map((group) => (
                        <div key={group.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-2xl">{group.icon}</span>
                            <div>
                              <h4 className="font-semibold text-gray-900">{group.name}</h4>
                              <p className="text-sm text-gray-500">{group.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <span className="text-sm text-gray-500">{group.memberCount} members</span>
                            <ChevronRight size={16} className="text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* User Details Sidebar */}
            {selectedUser && selectedTab === 'users' && (
              <div className="w-80 border-l border-gray-200 bg-gray-50 flex flex-col">
                <div className="p-5 border-b border-gray-200 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">User Details</h3>
                    <button onClick={() => setSelectedUser(null)} className="p-1 hover:bg-gray-100 rounded">
                      <X size={18} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xl font-medium">
                      {(selectedUser.firstName?.[0] || selectedUser.username[0]).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedUser.firstName && selectedUser.lastName
                          ? `${selectedUser.firstName} ${selectedUser.lastName}`
                          : selectedUser.username}
                      </p>
                      <p className="text-sm text-gray-500">@{selectedUser.username}</p>
                    </div>
                  </div>
                </div>
                <div className="flex-1 p-5 space-y-5 overflow-auto">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Email</label>
                    <p className="text-sm text-gray-900 flex items-center gap-2 mt-1">
                      <Mail size={14} className="text-gray-400" />
                      {selectedUser.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
                    <p className="mt-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${selectedUser.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                        {selectedUser.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Created</label>
                    <p className="text-sm text-gray-900 flex items-center gap-2 mt-1">
                      <Calendar size={14} className="text-gray-400" />
                      {selectedUser.createdTimestamp
                        ? new Date(selectedUser.createdTimestamp).toLocaleDateString()
                        : '-'}
                    </p>
                  </div>
                </div>
                <div className="p-5 border-t border-gray-200 bg-white space-y-2">
                  <button
                    onClick={() => openUserModal(selectedUser)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    <Edit size={16} />
                    Edit User
                  </button>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    <Key size={16} />
                    Reset Password
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Organization Modal */}
      {showOrgModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingOrg ? 'Edit Organization' : 'New Organization'}
              </h3>
              <button onClick={() => setShowOrgModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Name and Display Name in 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={orgFormData.name}
                    onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                    placeholder="my-organization"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={orgFormData.displayName}
                    onChange={(e) => setOrgFormData({ ...orgFormData, displayName: e.target.value })}
                    placeholder="My Organization"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
              {/* URL and Domain in 2 columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input
                    type="url"
                    value={orgFormData.url}
                    onChange={(e) => setOrgFormData({ ...orgFormData, url: e.target.value })}
                    placeholder="Enter the URL"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
                  <input
                    type="text"
                    value={orgFormData.domain}
                    onChange={(e) => setOrgFormData({ ...orgFormData, domain: e.target.value })}
                    placeholder="Enter the Domain"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
              {/* Logo upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    Choose File
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setOrgLogo(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </label>
                  <span className="text-sm text-gray-500">
                    {orgLogo ? orgLogo.name : 'No file chosen'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowOrgModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSaveOrg} disabled={actionLoading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Edit User' : 'Invite User'}
              </h3>
              <button onClick={() => setShowUserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={userFormData.firstName}
                    onChange={(e) => setUserFormData({ ...userFormData, firstName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={userFormData.lastName}
                    onChange={(e) => setUserFormData({ ...userFormData, lastName: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input
                  type="text"
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                  disabled={!!editingUser}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    value={userFormData.password}
                    onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowUserModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleSaveUser} disabled={actionLoading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : editingUser ? 'Save' : 'Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              <button onClick={() => setShowDeleteModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete{' '}
                <span className="font-semibold">
                  {deleteTarget.type === 'org'
                    ? (deleteTarget.item as Organization).name
                    : (deleteTarget.item as User).username}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={actionLoading} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Reset password for <span className="font-semibold">{selectedUser.username}</span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowPasswordModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button onClick={handleResetPassword} disabled={actionLoading || !newPassword} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">
                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
